/* ============================================================
   <cz-search> — Search modal custom element
   Open/close, focus management, state toggle (empty <-> typing),
   debounced predictive fetch (solo prodotti), keyboard.
   ============================================================ */

class CzSearch extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.input = null;
    this.panel = null;
    this.closeBtn = null;
    this.resultsContainer = null;
    this.countEl = null;
    this.ctaEl = null;
    this.seeAllEl = null;
    this.emptyEl = null;
    this.termEl = null;
    this.loadingEl = null;
    this.stateEmpty = null;
    this.stateTyping = null;
    this.currentController = null;
    this.debounceTimer = null;
    this.lastTrigger = null;

    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleInput = this.handleInput.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-cz-search-input]');
    this.panel = this.querySelector('.cz-search__panel');
    this.closeBtn = this.querySelector('[data-cz-search-close]');
    this.resultsContainer = this.querySelector('[data-cz-search-results]');
    this.metaEl = this.querySelector('[data-cz-search-meta]');
    this.countEl = this.querySelector('[data-cz-search-count]');
    this.ctaEl = this.querySelector('[data-cz-search-cta]');
    this.seeAllEl = this.querySelector('[data-cz-search-see-all]');
    this.emptyEl = this.querySelector('[data-cz-search-empty]');
    this.termEl = this.querySelector('[data-cz-search-term]');
    this.loadingEl = this.querySelector('[data-cz-search-loading]');
    this.stateEmpty = this.querySelector('[data-state="empty"]');
    this.stateTyping = this.querySelector('[data-state="typing"]');

    // Bind triggers anywhere in the DOM
    document.addEventListener('click', this.handleTriggerClick);
    this.closeBtn?.addEventListener('click', this.handleCloseClick);
    this.input?.addEventListener('input', this.handleInput);
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleTriggerClick);
    this.closeBtn?.removeEventListener('click', this.handleCloseClick);
    this.input?.removeEventListener('input', this.handleInput);
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.currentController) this.currentController.abort();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest('[data-cz-search-trigger]');
    if (!trigger) return;
    event.preventDefault();
    this.lastTrigger = trigger;
    this.open();
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  handleKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.hidden = false;
    document.body.classList.add('cz-search-open');
    if (this.lastTrigger) this.lastTrigger.setAttribute('aria-expanded', 'true');
    // Focus dopo paint
    requestAnimationFrame(() => this.input?.focus());
    this.swapState(this.input?.value ? 'typing' : 'empty');
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.hidden = true;
    document.body.classList.remove('cz-search-open');
    if (this.lastTrigger) {
      this.lastTrigger.setAttribute('aria-expanded', 'false');
      this.lastTrigger.focus();
    }
    if (this.input) this.input.value = '';
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
  }

  swapState(name) {
    if (name === 'typing') {
      if (this.stateEmpty) this.stateEmpty.hidden = true;
      if (this.stateTyping) this.stateTyping.hidden = false;
      this.classList.add('cz-search--typing');
    } else {
      if (this.stateEmpty) this.stateEmpty.hidden = false;
      if (this.stateTyping) this.stateTyping.hidden = true;
      this.classList.remove('cz-search--typing');
    }
  }

  handleInput(event) {
    const value = event.target.value.trim();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (value.length === 0) {
      this.swapState('empty');
      this.clearResults();
      return;
    }
    if (value.length < 2) {
      // resta in empty per evitare richieste rumorose
      return;
    }

    this.swapState('typing');
    this.debounceTimer = setTimeout(() => this.fetchPredictive(value), 200);
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
    if (this.countEl) this.countEl.textContent = '';
    if (this.ctaEl) this.ctaEl.hidden = true;
    if (this.emptyEl) this.emptyEl.hidden = true;
  }

  fetchPredictive(value) {
    if (this.currentController) this.currentController.abort();
    this.currentController = new AbortController();

    const query = encodeURIComponent(value.slice(0, 100));
    // Preview: 4 prodotti per il pannello (limit=4)
    const suggestUrl = `/search/suggest.json?q=${query}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;
    // Total count reale: rendered da templates/search.count.liquid (layout none → solo numero)
    const countUrl = `/search?q=${query}&type=product&view=count`;

    if (this.loadingEl) this.loadingEl.hidden = false;

    Promise.all([
      fetch(suggestUrl, { signal: this.currentController.signal }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }),
      fetch(countUrl, { signal: this.currentController.signal })
        .then((res) => (res.ok ? res.text() : ''))
        .catch(() => '')
    ])
      .then(([data, countText]) => {
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        // count.liquid output può contenere whitespace residuo del layout default
        // se {% layout none %} fallisce — estrai il primo integer ragionevole.
        const match = String(countText).match(/\d+/);
        const totalCount = match ? parseInt(match[0], 10) : products.length;
        this.renderResults(products, value, totalCount);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        // network error → mostra empty
        this.renderResults([], value, 0);
      })
      .finally(() => {
        if (this.loadingEl) this.loadingEl.hidden = true;
      });
  }

  renderResults(products, query, totalCount) {
    if (!this.resultsContainer) return;
    // Fallback: se totalCount mancante o invalido, usa il numero di prodotti restituiti.
    const total = Number.isInteger(totalCount) && totalCount >= 0 ? totalCount : products.length;

    if (products.length === 0) {
      this.resultsContainer.innerHTML = '';
      if (this.countEl) this.countEl.textContent = '';
      if (this.ctaEl) this.ctaEl.hidden = true;
      if (this.metaEl) this.metaEl.hidden = true;
      if (this.termEl) this.termEl.textContent = query;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    if (this.metaEl) this.metaEl.hidden = false;

    // Mostra max 4 card nel pannello — il resto sta dietro al pulsante "Vedi tutti".
    const PREVIEW_LIMIT = 4;
    const visible = products.slice(0, PREVIEW_LIMIT);
    const html = visible.map((p) => this.productCardHTML(p)).join('');
    this.resultsContainer.innerHTML = html;

    if (this.countEl) {
      // Header del pannello mostra il TOTALE reale (non solo i 4 visibili).
      this.countEl.textContent = this.formatCount(total);
    }
    if (this.ctaEl && this.seeAllEl) {
      // CTA visibile solo se ci sono risultati extra oltre ai 4 in preview.
      if (total > visible.length) {
        this.ctaEl.hidden = false;
        this.seeAllEl.href = `/search?q=${encodeURIComponent(query)}&type=product`;
        this.seeAllEl.textContent = this.formatSeeAll(total);
      } else {
        this.ctaEl.hidden = true;
      }
    }
  }

  productCardHTML(p) {
    const url = p.url || '#';
    // Lowercase source: il CSS applica text-transform: capitalize → matcha
    // esattamente il pattern di card-product.liquid (title | downcase + CSS capitalize).
    const titleSrc = (p.title || '').toLowerCase();
    const title = this.escapeHtml(titleSrc);
    const titleAttr = this.escapeAttr(p.title || '');
    const vendor = this.escapeHtml((p.vendor || '').toLowerCase());
    const image = p.featured_image
      ? `<img src="${this.escapeAttr(p.featured_image.url || p.image || '')}" alt="${titleAttr}" loading="lazy" width="300" height="300">`
      : (p.image ? `<img src="${this.escapeAttr(p.image)}" alt="${titleAttr}" loading="lazy" width="300" height="300">` : '');
    const price = (p.price != null && p.price !== '')
      ? this.formatPrice(p.price)
      : '';

    return `
      <a class="cz-search__product" href="${this.escapeAttr(url)}">
        <div class="cz-search__product-img">${image}</div>
        <div class="cz-search__product-meta">
          ${vendor ? `<span class="cz-search__product-vendor">${vendor}</span>` : ''}
          <span class="cz-search__product-title">${title}</span>
          ${price ? `<span class="cz-search__product-price">${price}</span>` : ''}
        </div>
      </a>
    `;
  }

  formatPrice(price) {
    // Output sempre "€ N" italiano (intero, separatore migliaia '.').
    // Accetta number (euros, es. 470 → "€ 470") oppure string da Shopify
    // suggest.json (es. "470.00", "1.234,50", "€ 1.234,50" → tutti → "€ 1.234").
    if (price == null || price === '') return '';
    let euros;
    if (typeof price === 'number') {
      euros = price;
    } else {
      const cleaned = String(price).replace(/[^\d.,]/g, '');
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      if (lastComma > lastDot && (cleaned.length - lastComma - 1) === 2) {
        // Italian decimal "1.234,50": punti = thousands, virgola = decimal
        euros = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
      } else if (lastDot > lastComma && (cleaned.length - lastDot - 1) === 2) {
        // US decimal "1,234.50" o "470.00": virgole = thousands, punto = decimal
        euros = parseFloat(cleaned.replace(/,/g, ''));
      } else {
        // No decimals: integer (separatori = thousands)
        euros = parseFloat(cleaned.replace(/[.,]/g, ''));
      }
    }
    if (isNaN(euros)) return '';
    const intPart = Math.round(euros)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `€ ${intPart}`;
  }

  formatCount(n) {
    return n === 1 ? `${n} risultato` : `${n} risultati`;
  }

  formatSeeAll(count) {
    return count === 1 ? 'Vedi 1 risultato' : `Vedi tutti i ${count} risultati`;
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  escapeAttr(s) { return this.escapeHtml(s); }
}

customElements.define('cz-search', CzSearch);
